import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import { useLang } from '../LangContext';
import { useT } from '../i18n';
import { getMember } from '../api';

const s = {
  page: {
    backgroundColor: '#FFF5EE',
    minHeight: '100dvh',
    paddingTop: 56,
    paddingBottom: 96,
    fontFamily: "'Be Vietnam Pro', sans-serif",
  },
  content: {
    padding: '24px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
};

export default function HomeScreen() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const t = useT(lang);

  const [member, setMember] = useState(null);

  useEffect(() => {
    getMember().then(setMember).catch(console.error);
  }, []);

  const memberName = member?.name || '林珍珠';
  const memberPoints = member?.points ?? 1250;
  const memberTokens = member?.tokens ?? 0;
  const avatarChar = memberName.charAt(1) || memberName.charAt(0);
  const recentTxns = member?.recentTransactions || [];

  return (
    <div style={s.page}>
      <TopBar />
      <div style={s.content}>

        {/* ── Member Card ─────────────────────────────── */}
        <div style={{ position: 'relative' }}>
          {/* Decorative blur */}
          <div style={{
            position: 'absolute', top: -16, right: -16,
            width: 96, height: 96,
            background: 'rgba(249,204,97,0.2)',
            borderRadius: '50%', filter: 'blur(24px)',
          }}/>
          <div style={{
            position: 'relative',
            borderRadius: 24,
            border: '2px solid #FFD166',
            background: 'linear-gradient(135deg, #FFF9F2 0%, #ffffff 100%)',
            padding: 24,
            boxShadow: '0px 12px 32px rgba(45,45,66,0.06)',
            overflow: 'hidden',
          }}>
            {/* Avatar + name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                backgroundColor: '#f9cc61',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 700, fontSize: 20, color: '#FF5C36',
                flexShrink: 0,
              }}>{avatarChar}</div>
              <div>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 700, fontSize: 16, color: '#2d2d42',
                }}>{memberName}</div>
                <div style={{ fontSize: 12, color: '#5a5971', fontWeight: 500 }}>
                  {t('memberSince')}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: 'rgba(172,170,197,0.2)', marginBottom: 20 }}/>

            {/* Points */}
            <div>
              <div style={{ fontSize: 12, color: '#5a5971', fontWeight: 500, marginBottom: 4 }}>
                {t('availablePoints')}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                <span style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 800, fontSize: 32, color: '#2d2d42', lineHeight: 1,
                }}>{memberPoints.toLocaleString()}</span>
                <span style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 700, fontSize: 16, color: '#2d2d42',
                }}>{t('pointsUnit')}</span>
              </div>
            </div>

            {/* Star decoration */}
            <div style={{
              position: 'absolute', bottom: -10, right: -10, opacity: 0.1,
            }}>
              <svg width="100" height="100" viewBox="0 0 24 24" fill="#2d2d42" style={{ transform: 'rotate(12deg)' }}>
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
          </div>
        </div>

        {/* ── Activity Qualifications ─────────────────── */}
        <section>
          <h3 style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 800, fontSize: 14, color: '#2d2d42',
            marginBottom: 12,
          }}>{t('myQualifications')}</h3>

          {memberTokens > 0 ? (
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: 16,
              borderLeft: '4px solid #f9cc61',
              padding: 16,
              boxShadow: '0px 4px 12px rgba(45,45,66,0.04)',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 700, fontSize: 14, color: '#2d2d42',
                }}>{t('eventNameHome')}</div>
                <span style={{
                  padding: '3px 8px',
                  background: '#E6F8F4',
                  color: '#006854',
                  fontSize: 10, fontWeight: 700,
                  borderRadius: 8,
                  border: '1px solid rgba(0,104,84,0.1)',
                  whiteSpace: 'nowrap',
                }}>{t('statusAvailable')} ×{memberTokens}</span>
              </div>
              <div style={{ fontSize: 12, color: '#5a5971', fontWeight: 500, marginBottom: 16 }}>
                {t('validUntil')}
              </div>
              <button
                onClick={() => navigate('/confirm?mode=token')}
                style={{
                  width: '100%', height: 44,
                  background: 'transparent',
                  border: '2px solid #FF5C36',
                  color: '#FF5C36',
                  borderRadius: 12,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 700, fontSize: 14,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {t('useQualification')}
              </button>
            </div>
          ) : (
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: 16,
              borderLeft: '4px solid #e8e5ff',
              padding: 16,
              boxShadow: '0px 4px 12px rgba(45,45,66,0.04)',
              opacity: 0.6,
            }}>
              <div style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 700, fontSize: 14, color: '#5a5971', marginBottom: 4,
              }}>{t('eventNameHome')}</div>
              <div style={{ fontSize: 12, color: '#5a5971' }}>{t('noQualification') || '目前無活動資格'}</div>
            </div>
          )}
        </section>

        {/* ── Primary CTA ─────────────────────────────── */}
        <button
          onClick={() => navigate('/confirm?mode=points')}
          style={{
            width: '100%', height: 52,
            background: 'linear-gradient(180deg, #FF5C35 0%, #e64d28 100%)',
            color: '#ffffff',
            border: 'none',
            borderRadius: 14,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 800, fontSize: 18,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: '0 4px 20px rgba(255,92,53,0.3), inset 0 2px 4px rgba(255,255,255,0.25)',
            transition: 'transform 0.15s',
          }}
          onMouseDown={e => e.currentTarget.style.transform = 'scale(0.97)'}
          onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <span>{t('redeemWithPoints')}</span>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9.19 6.35c-2.04 2.29-3.44 5.58-3.57 5.65-.08.04 1.08.54 2.59 1.11l-.18.44c-.39 1.03-.39 2.16 0 3.19l.55 1.46 1.57-1.17c.87-.65 1.82-1.15 2.82-1.49L21 19l-2-14-9.81 1.35z"/>
          </svg>
        </button>

        {/* ── Recent Redemptions ──────────────────────── */}
        <section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800, fontSize: 13, color: '#2d2d42',
            }}>{t('recentRedemptions')}</h3>
            <button
              onClick={() => navigate('/event')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#FF5C36', fontSize: 12, fontWeight: 700,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >{t('viewAll')}</button>
          </div>

          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: 24,
            overflow: 'hidden',
            boxShadow: '0px 4px 12px rgba(45,45,66,0.04)',
          }}>
            {recentTxns.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#5a5971', fontSize: 13 }}>
                {t('noTransactions') || '尚無交易記錄'}
              </div>
            ) : recentTxns.slice(0, 3).map((txn, i, arr) => {
              const pts = txn.pointsChange;
              const ptsLabel = pts > 0 ? `+${pts} 點` : pts < 0 ? `${pts} 點` : '—';
              const ptsColor = pts > 0 ? '#006854' : pts < 0 ? '#b41340' : '#5a5971';
              const dateStr = txn.createdAt ? txn.createdAt.slice(0, 10).replace(/-/g, '/') : '';
              return (
                <div
                  key={i}
                  onClick={() => navigate('/event')}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '16px 20px',
                    borderBottom: i < arr.length - 1 ? '1px solid rgba(232,229,255,0.5)' : 'none',
                    cursor: 'pointer',
                  }}
                >
                  <div>
                    <div style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontWeight: 700, fontSize: 13, color: '#2d2d42', marginBottom: 2,
                    }}>{txn.description}</div>
                    <div style={{ fontSize: 11, color: '#5a5971' }}>{dateStr}</div>
                  </div>
                  <span style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 800, fontSize: 14, color: ptsColor,
                  }}>{ptsLabel}</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Lucky Tea (Gamified Element) ────────────── */}
        <div style={{
          padding: 20,
          borderRadius: 24,
          background: 'rgba(249,204,97,0.1)',
          border: '1px solid rgba(249,204,97,0.3)',
          display: 'flex', alignItems: 'center', gap: 16,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 16,
            backgroundColor: '#f9cc61',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transform: 'rotate(3deg)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            flexShrink: 0,
          }}>
            {/* Dice icon */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#443100">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM7 9c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm0 6c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm5 3c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm0-6c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm5 3c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-5c0-.55.45-1 1-1s1 .45 1 1-.45 1-1 1-1-.45-1-1z"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#5b4400' }}>
              {t('luckyTea')}
            </div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800, fontSize: 14, color: '#2d2d42', marginTop: 2,
            }}>{t('luckyTeaName')}</div>
          </div>
        </div>

      </div>
      <BottomNav />
    </div>
  );
}
